import { isConditionMetricFlagged } from '@repo/utils';
import type { SingleConditionMetric } from 'services/conditions/useConditionMetrics';

const FLAG_COLOR_RANK: Record<string, number> = { red: 0, orange: 1 };
const NON_FLAG_COLOR_RANK: Record<string, number> = { grey: 0, green: 1 };

export type ConditionSectionGroup = {
  section: string;
  metrics: SingleConditionMetric[];
};

/**
 * Group metrics by key_insights_section — sections ordered by section priority;
 * within a section, by category order (section_order) then the color ranking.
 */
const groupBySection = (
  metrics: SingleConditionMetric[],
  colorRank: Record<string, number>,
): ConditionSectionGroup[] => {
  const groups = new Map<
    string,
    ConditionSectionGroup & { sectionPriority: number }
  >();

  metrics.forEach(metric => {
    const key = metric.section ?? '';
    const existing = groups.get(key);
    if (existing) {
      existing.metrics.push(metric);
    } else {
      groups.set(key, {
        section: key,
        sectionPriority: metric.section_priority ?? Number.NEGATIVE_INFINITY,
        metrics: [metric],
      });
    }
  });

  return [...groups.values()]
    .sort((a, b) => b.sectionPriority - a.sectionPriority)
    .map(group => ({
      section: group.section,
      metrics: [...group.metrics].sort(
        (a, b) =>
          (a.section_order ?? Number.MAX_SAFE_INTEGER) -
            (b.section_order ?? Number.MAX_SAFE_INTEGER) ||
          (colorRank[a.evaluation_color] ?? 99) -
            (colorRank[b.evaluation_color] ?? 99),
      ),
    }));
};

export function groupConditionMetrics(metrics: SingleConditionMetric[]) {
  const flaggedMetrics: SingleConditionMetric[] = [];
  const nonFlaggedMetrics: SingleConditionMetric[] = [];

  for (const metric of metrics) {
    if (isConditionMetricFlagged(metric)) flaggedMetrics.push(metric);
    else nonFlaggedMetrics.push(metric);
  }

  return {
    flaggedMetrics: groupBySection(flaggedMetrics, FLAG_COLOR_RANK),
    nonFlaggedMetrics: groupBySection(nonFlaggedMetrics, NON_FLAG_COLOR_RANK),
  };
}
